import React from 'react';
import { Box, CircularProgress, Typography, Skeleton } from '@mui/material';
import { motion } from 'framer-motion';

// Loading básico
const Loading = ({ 
  message = 'Carregando...', 
  size = 40,
  variant = 'circular',
  fullScreen = false 
}) => {
  const content = (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <Box 
        display="flex" 
        flexDirection="column" 
        alignItems="center" 
        justifyContent="center" 
        gap={2}
        p={3}
      >
        <CircularProgress 
          size={size} 
          thickness={4}
          sx={{
            color: 'primary.main',
            animationDuration: '1s'
          }}
        />
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{ fontWeight: 500 }}
        >
          {message}
        </Typography>
      </Box>
    </motion.div>
  );

  if (fullScreen) {
    return (
      <Box 
        display="flex" 
        alignItems="center" 
        justifyContent="center" 
        minHeight="100vh"
        bgcolor="background.default"
      >
        {content}
      </Box>
    );
  }

  return content;
};

// Loading para cards
export const CardLoading = ({ height = 200 }) => (
  <Box p={2}>
    <Skeleton variant="text" height={32} width="60%" sx={{ mb: 1 }} />
    <Skeleton variant="text" height={20} width="40%" sx={{ mb: 2 }} />
    <Skeleton variant="rectangular" height={height} sx={{ borderRadius: 2 }} />
  </Box>
);

// Loading para tabelas
export const TableLoading = ({ rows = 5, columns = 4 }) => (
  <Box>
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <Box key={rowIndex} display="flex" gap={2} p={2}>
        {Array.from({ length: columns }).map((_, colIndex) => (
          <Skeleton 
            key={colIndex}
            variant="text" 
            height={24} 
            sx={{ flex: 1 }}
          />
        ))}
      </Box>
    ))}
  </Box>
);

// Loading para lista
export const ListLoading = ({ items = 5 }) => (
  <Box>
    {Array.from({ length: items }).map((_, index) => (
      <Box key={index} display="flex" alignItems="center" gap={2} p={2}>
        <Skeleton variant="circular" width={40} height={40} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" height={20} width="70%" />
          <Skeleton variant="text" height={16} width="50%" />
        </Box>
      </Box>
    ))}
  </Box>
);

// Loading para formulário
export const FormLoading = () => (
  <Box p={2} display="flex" flexDirection="column" gap={2}>
    <Skeleton variant="text" height={32} width="30%" />
    <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
    <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
    <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
    <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
      <Skeleton variant="rectangular" width={100} height={36} sx={{ borderRadius: 1 }} />
      <Skeleton variant="rectangular" width={100} height={36} sx={{ borderRadius: 1 }} />
    </Box>
  </Box>
);

// Loading para dashboard
export const DashboardLoading = () => (
  <Box p={3}>
    {/* Header */}
    <Box mb={4}>
      <Skeleton variant="text" height={40} width="40%" />
      <Skeleton variant="text" height={20} width="60%" />
    </Box>
    
    {/* Stats Cards */}
    <Box display="flex" gap={3} mb={4}>
      {Array.from({ length: 4 }).map((_, index) => (
        <Box key={index} flex={1}>
          <CardLoading height={120} />
        </Box>
      ))}
    </Box>
    
    {/* Chart Area */}
    <Box display="flex" gap={3}>
      <Box flex={2}>
        <CardLoading height={300} />
      </Box>
      <Box flex={1}>
        <CardLoading height={300} />
      </Box>
    </Box>
  </Box>
);

// Loading com overlay
export const OverlayLoading = ({ message = 'Processando...', visible = true }) => {
  if (!visible) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}
    >
      <Box 
        bgcolor="background.paper"
        borderRadius={4}
        p={4}
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap={2}
        boxShadow={3}
      >
        <CircularProgress size={48} thickness={4} />
        <Typography variant="h6" color="text.primary">
          {message}
        </Typography>
      </Box>
    </motion.div>
  );
};

// Loading para botões
export const ButtonLoading = ({ loading = false, children, ...props }) => (
  <Box position="relative" display="inline-flex">
    <motion.div
      animate={{ opacity: loading ? 0.6 : 1 }}
      transition={{ duration: 0.2 }}
    >
      {children}
    </motion.div>
    {loading && (
      <CircularProgress
        size={24}
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          marginTop: '-12px',
          marginLeft: '-12px',
        }}
      />
    )}
  </Box>
);

export default Loading;






